import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

const CENTER = 110;
const INNER_R = 70;
const OUTER_R = 100;

const pad = (n) => String(n).padStart(2, "0");

// 12 positions around the dial, starting at 12 o'clock.
const ringPoint = (i, r) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    return { x: CENTER + r * Math.cos(angle), y: CENTER + r * Math.sin(angle) };
};

// Inner ring: 12, 1..11. Outer ring: 00, 13..23.
const INNER_HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const OUTER_HOURS = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

const BASE_MINUTES = [0, 15, 30, 45];

/**
 * 24-hour clock-face picker for an episode's release time.
 * `value` is a local "YYYY-MM-DDTHH:mm" string; onSave receives the same format.
 */
const ClockTimeModal = ({ show, episode, number, onClose, onSave }) => {
    const [hour, setHour] = useState(8);
    const [minute, setMinute] = useState(0);

    useEffect(() => {
        if (!show || !episode?.start_date_time) return;
        const d = new Date(episode.start_date_time);
        if (isNaN(d.getTime())) return;
        setHour(d.getHours());
        setMinute(d.getMinutes());
    }, [show, episode]);

    if (!episode) return null;

    const start = new Date(episode.start_date_time);
    const validDate = !isNaN(start.getTime());
    const dateLabel = validDate
        ? start.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
        : "";
    const minutes = BASE_MINUTES.includes(minute)
        ? BASE_MINUTES
        : [...BASE_MINUTES, minute].sort((a, b) => a - b);

    const handleSave = () => {
        if (!validDate) return;
        const datePart = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
        onSave(episode.id, `${datePart}T${pad(hour)}:${pad(minute)}`);
        onClose();
    };

    const renderRing = (hours, radius) =>
        hours.map((h, i) => {
            const { x, y } = ringPoint(i, radius);
            const label = radius === OUTER_R || h === 0 ? pad(h) : String(h);
            return (
                <button
                    key={`${radius}-${h}`}
                    type="button"
                    className={`clock-hour${hour === h ? " selected" : ""}`}
                    style={{ left: x, top: y }}
                    aria-label={`${pad(h)} hours`}
                    aria-pressed={hour === h}
                    onClick={() => setHour(h)}
                >
                    {label}
                </button>
            );
        });

    return (
        <Modal show={show} onHide={onClose} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title className="fs-6">
                    <span className="clock-chip me-2">{number}</span>{episode.title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                {dateLabel && <p className="text-muted mb-1">{dateLabel}</p>}
                <div className="clock-readout">{pad(hour)}:{pad(minute)}</div>
                <p className="text-muted small">
                    {number === 1
                        ? "This is your club launch. Listeners get access to the club on this date, and the weekly rhythm runs from here."
                        : "The exact moment this episode drops into the club. Listeners get it at this time in their own timezone."}
                </p>
                <div className="clock-face" role="group" aria-label="Hour">
                    {renderRing(OUTER_HOURS, OUTER_R)}
                    {renderRing(INNER_HOURS, INNER_R)}
                </div>
                <Form.Group className="mt-3 d-flex align-items-center justify-content-center gap-2" controlId="clockMinutes">
                    <Form.Label className="mb-0 small text-muted">Minutes</Form.Label>
                    <Form.Select
                        size="sm"
                        style={{ width: "auto" }}
                        value={minute}
                        onChange={(e) => setMinute(parseInt(e.target.value, 10))}
                    >
                        {minutes.map((m) => <option key={m} value={m}>{pad(m)}</option>)}
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={!validDate}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ClockTimeModal;
